import { Person } from '@mui/icons-material';
import { Divider, useMediaQuery } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import {
  AnalyticsReportEmployeeScope,
  AnalyticsReportProps,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  usePositionChangesReportData,
} from '~/data/reports';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  useReportDataset,
} from '~/scenes/PortalModules/Analytics/common';

import { usePositionChangesReport } from './usePositionChangesReport';

export const PositionChangesReport = () => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery('(max-height:650px)');
  const loggedInEmployeeId = useLoggedInEmployeeId();
  const apiRef = useGridApiRef();

  const defaultReportSelection: AnalyticsReportProps<object> = {
    reportType: {
      type: AnalyticsReportTypes.Team,
      role: AnalyticsReportRoleScope.Manager,
      employeeId: loggedInEmployeeId,
      scope: AnalyticsReportScope.Direct,
    },
    params: {
      startDate: moment().startOf('year').toDate(),
      endDate: moment().toDate(),
      empScope: AnalyticsReportEmployeeScope.AllEmployees,
    },
  };

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
      },
    },
  });

  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = usePositionChangesReportData<object>(reportDatasetProps.reportSelection);

  const { table } = usePositionChangesReport({
    data,
    startDate:
      reportDatasetProps.reportSelection.params?.startDate ??
      moment().startOf('year').toDate(),
    endDate:
      reportDatasetProps.reportSelection.params?.endDate ?? moment().toDate(),
  });

  return (
    <ReportViewLayout
      heading={t('laPosChanges')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout showHeading={true}>
              <ReportDatasetSelector
                {...reportDatasetProps}
                isLoading={isLoadingReportData}
                failedToLoad={failedToLoad}
                renderCurrentParams={({ params }) => (
                  <>
                    {`${moment(params?.startDate).format('L')} - ${moment(
                      params?.endDate,
                    ).format('L')}`}
                  </>
                )}
                renderParamsFields={({ params, onChange }) => (
                  <ReportDateRangeSelector
                    startDate={params.startDate}
                    endDate={params.endDate}
                    onRangeChange={(startDate, endDate) =>
                      onChange({
                        ...params,
                        startDate: startDate ?? params.startDate,
                        endDate: endDate ?? params.endDate,
                      })
                    }
                    mode={ReportDateRangeSelectorMode.DateRange}
                  />
                )}
              />
              <ReportDatasetRowCount
                count={table.rows.length}
                description={t('laTotalRecords')}
                icon={<Person />}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              <ReportColumnsVisibility
                apiRef={apiRef}
                isLoading={isLoadingReportData}
                columnGroupingModel={table.columnGroupingModel}
              />
              <ReportDataExport
                title={t('laPosChanges')}
                startDate={reportDatasetProps.reportSelection.params?.startDate}
                apiRef={apiRef}
                isLoading={isLoadingReportData}
              />
            </>
          ),
        },
      }}
      slotProps={{
        content: {
          sx: {
            minHeight: isSmallScreen ? 'calc(100vh - 100px)' : 0,
          },
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={table.columnConfigs}
        rows={table.rows}
        getRowId={(row) => row.rowId}
        pinnedColumns={{
          left: ['employeeId', 'firstName', 'lastName'],
        }}
        columnGroupingModel={table.columnGroupingModel}
        rowSpanning={true}
        density={'compact'}
      />
    </ReportViewLayout>
  );
};
